import React, { useContext, useEffect, useState } from 'react';
import Modal from '@atlaskit/modal-dialog';
import styled from 'styled-components';

import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';
import Row from 'components/Common/Row';
import CloudFunctionClientContext from 'contexts/CloudFunctionClientContext';
import ToastContext from 'contexts/ToastContext';
import { AgentKbPreview, previewAgentKb } from 'services/agentKb';

// The exact always-on system text the draft would give the agent, composed by
// the same code the runtime uses. Every other view here is a proxy for this —
// a token count says how much, this says what.

const ContentDiv = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 70vh;
`;

const BlockPre = styled.pre`
  flex: 1;
  overflow: auto;
  white-space: pre-wrap;
  font-family: monospace;
  font-size: 12px;
  color: var(--color-text-primary);
  background-color: var(--color-bg-secondary);
  border-radius: 8px;
  padding: 16px;
  margin: 0;
`;

const AlwaysOnPreviewDialog = ({ close }: { close: () => void }) => {
  const { api } = useContext(CloudFunctionClientContext);
  const { showToast } = useContext(ToastContext);
  const [preview, setPreview] = useState<AgentKbPreview | null>(null);

  useEffect(() => {
    let isActive = true;
    previewAgentKb(api).then((result) => {
      if (!isActive) {
        return;
      }
      if (result.ok) {
        setPreview(result.data);
      } else {
        showToast(result.error);
      }
    });
    return () => {
      isActive = false;
    };
  }, [api, showToast]);

  return (
    <Modal width="x-large" onClose={close} shouldScrollInViewport>
      <ContentDiv data-testid="agent-kb-always-on-preview">
        <Row centerAlign spaceBetween>
          <Typography.Header type="H4">
            What every answer starts with
          </Typography.Header>
          <Button type="Ghost" size="Small" label="Close" onClick={close} />
        </Row>
        {!preview && (
          <Typography.Body type="Body 13">Composing…</Typography.Body>
        )}
        {preview && (
          <>
            <Typography.Body type="Body 12" color="var(--color-text-secondary)">
              {preview.contextDocs.length} context doc(s),{' '}
              {preview.skills.length} always-on workflow skill(s),{' '}
              {preview.domains.length} domain(s) loadable on demand,{' '}
              {preview.topics.length} topic(s) in the index.
            </Typography.Body>
            <BlockPre>{preview.alwaysOnBlock}</BlockPre>
          </>
        )}
      </ContentDiv>
    </Modal>
  );
};

export default AlwaysOnPreviewDialog;
